import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Typography, message, Checkbox } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services';
import type { LoginRequest } from '../../types';
import './style.css';

const { Title } = Typography;

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  // 不使用useAuth钩子，避免在登录页面触发不必要的认证检查

  // 本地存储的key
  const STORAGE_KEYS = {
    username: 'remembered_username',
    password: 'remembered_password',
    remember: 'remember_credentials'
  };

  // 初始化时读取本地存储的凭据
  useEffect(() => {
    const savedUsername = localStorage.getItem(STORAGE_KEYS.username);
    const savedPassword = localStorage.getItem(STORAGE_KEYS.password);
    const shouldRemember = localStorage.getItem(STORAGE_KEYS.remember) === 'true';
    
    if (shouldRemember && savedUsername && savedPassword) {
      form.setFieldsValue({
        username: savedUsername,
        password: savedPassword
      });
      setRememberMe(true);
    }
  }, [form, STORAGE_KEYS]);

  const handleSubmit = async (values: LoginRequest) => {
    try {
      setLoading(true);
      
      // 直接调用认证服务，避免使用useAuth钩子
      const response = await authService.login(values);
      
      // 保存token和用户信息
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user_info', JSON.stringify(response.user));
      
      // 处理记住密码功能
      if (rememberMe) {
        localStorage.setItem(STORAGE_KEYS.username, values.username);
        localStorage.setItem(STORAGE_KEYS.password, values.password);
        localStorage.setItem(STORAGE_KEYS.remember, 'true');
      } else {
        localStorage.removeItem(STORAGE_KEYS.username);
        localStorage.removeItem(STORAGE_KEYS.password);
        localStorage.removeItem(STORAGE_KEYS.remember);
      }
      
      message.success(response.message || '登录成功');
      
      // 根据用户角色跳转到不同页面
      if (response.user.is_admin) {
        navigate('/admin/dashboard');
      } else {
        navigate('/employee/dashboard');
      }
    } catch (error: any) {
      console.error('登录失败:', error);
      // 错误信息会由axios拦截器处理
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Card className="login-card">
        <div className="login-header">
          <Title level={2}>数据管理系统</Title>
          <p>请输入您的登录信息</p>
        </div>
        
        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名!' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
            />
          </Form.Item>

          <Form.Item>
            <Checkbox 
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            >
              记住用户名和密码
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="login-button"
              loading={loading}
              block
            >
              登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
